import React, { useMemo, useState } from "react"

import { OneVerifyCommentType } from "@common-shared/config/oneVerify/oneVerifyPolicies.config.ts"
import {
    IPatientIdentificationData,
    IPolicyIdentificationData,
} from "@common-shared/types/policies.types.ts"
import { IOneVerifyPolicyComment } from "@common-shared/types/responseData/oneVerifyResponseData.types.ts"

import { SpinnerElement } from "@front-shared/elements/spinner.element.tsx"

import { PolicySidebarViewMode } from "../../../../config/policies.config"
import { RuntimeHelper } from "../../../../helpers/runtime.helper"
import { useAppSelector } from "../../../../store/selectors/app.selector"
import { policiesSelector } from "../../../../store/selectors/policies.selector"
import { runtimeSelector } from "../../../../store/selectors/runtime.selector"
import { AddCommentComponent } from "./addComment.component"
import { SystemCommentCardComponent } from "./systemCommentCard.component"
import { UserCommentCardComponent } from "./userCommentCard.component"

export const PolicySidebarCommentsComponent: React.FunctionComponent<
    IPolicySidebarCommentsComponentProps
> = props => {
    const runtimeState = useAppSelector(runtimeSelector)
    const [replyToComment, setReplyToComment] =
        useState<IOneVerifyPolicyComment>()
    const [editComment, setEditComment] = useState<IOneVerifyPolicyComment>()

    const { selectedItemComments } = useAppSelector(policiesSelector)

    const isListLoading = useMemo<boolean>(() => {
        return RuntimeHelper.isGetPolicyCommentsLoading()
    }, [runtimeState.isLoading])

    const onClearReplyToComment = () => {
        setReplyToComment(undefined)
    }

    const onClearEditComment = () => {
        setEditComment(undefined)
    }

    return (
        // We want this component to be wrapped in a fragment, so we'll have to hide each section separately using the viewMode and selectedTab
        <>
            <div
                className={`
                    flex-1 min-h-0 overflow-y-auto flex-col-reverse
                    ${
                        !props.isCommentsTabOpen
                            ? "hidden"
                            : props.viewMode === PolicySidebarViewMode.CLOSED
                              ? "hidden md:flex"
                              : "flex"
                    }
                    ${
                        !selectedItemComments.length && isListLoading
                            ? "justify-center items-center"
                            : ""
                    }
                `}
            >
                {selectedItemComments.map((comment, index) => {
                    switch (comment.type) {
                        case OneVerifyCommentType.USER:
                            return (
                                <UserCommentCardComponent
                                    key={index}
                                    className="mb-[16px]"
                                    item={comment}
                                    onReplyToComment={setReplyToComment}
                                    onEditComment={setEditComment}
                                />
                            )

                        case OneVerifyCommentType.SYSTEM:
                            return (
                                <SystemCommentCardComponent
                                    key={index}
                                    className="mb-[16px]"
                                    item={comment}
                                />
                            )
                    }
                })}

                {!selectedItemComments.length && isListLoading ? (
                    <SpinnerElement />
                ) : null}
            </div>

            <AddCommentComponent
                className={
                    !props.isCommentsTabOpen
                        ? "hidden"
                        : props.viewMode === PolicySidebarViewMode.CLOSED
                          ? "hidden md:block"
                          : "block"
                }
                policy={props.policy}
                patient={props.patient}
                replyToComment={replyToComment}
                editComment={editComment}
                onClearReplyToComment={onClearReplyToComment}
                onClearEditComment={onClearEditComment}
                onCommentAdded={props.onCommentAdded}
            />
        </>
    )
}

interface IPolicySidebarCommentsComponentProps {
    className?: string
    viewMode: PolicySidebarViewMode
    isCommentsTabOpen: boolean
    policy: IPolicyIdentificationData
    patient?: IPatientIdentificationData
    onCommentAdded: (newComment: IOneVerifyPolicyComment) => void
}
